import { useAtom } from 'jotai';
import { type RefObject, useCallback, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { atomWithStorage } from '../../common/providers/StorageProvider';
import { StorageKey } from '../../common/types/Storage';

const allMessageDraftsAtom = atomWithStorage<Record<string, string>>(
    StorageKey.ALL_MESSAGE_DRAFTS,
    {},
);

// Sets up an atom to store drafted messages, and provides functions to set and clear the draft
export const useInitLocalMessageDrafts = (channelUrl: string) => {
    const [allMessageDrafts, setAllMessageDrafts] =
        useAtom(allMessageDraftsAtom);

    const draftMessage = allMessageDrafts[channelUrl] ?? '';

    const setDraftMessage = useCallback(
        (message: string) => {
            if (message.trim() === '') {
                clearDraftMessage();
                return;
            }

            setAllMessageDrafts((prev) => ({
                ...prev,
                [channelUrl]: message,
            }));
        },
        [channelUrl],
    );

    const clearDraftMessage = useCallback(() => {
        setAllMessageDrafts((prev) => {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            prev[channelUrl] != null && delete prev[channelUrl];
            return { ...prev };
        });
    }, [channelUrl]);

    return { draftMessage, setDraftMessage, clearDraftMessage };
};

// Hooks into the sendbird message input (using useGroupChannelContext) to apply a stored draft and save new drafts
export const useLocalMessageDrafts = (
    channelUrl: string,
    messageInputRef: RefObject<HTMLDivElement>,
    initialMessage: string,
    onMessageChange: (msg: string) => void,
) => {
    const storeMessageDraft = useDebouncedCallback(
        useCallback(
            (e: KeyboardEvent) => {
                const messageInputElement = e.target as HTMLDivElement | null;
                if (messageInputElement != null) {
                    onMessageChange(messageInputElement.innerText);
                }
            },
            [onMessageChange],
        ),
        1000,
    );

    useEffect(() => {
        const messageInputElement = messageInputRef.current;
        if (messageInputElement == null) {
            return;
        }

        // Event listeners to store draft message and delete draft on send
        messageInputElement.addEventListener('keyup', storeMessageDraft);

        return () => {
            messageInputElement.removeEventListener('keyup', storeMessageDraft);
        };
    }, []);

    useEffect(() => {
        const messageInputElement = messageInputRef.current;
        if (messageInputElement == null) {
            return;
        }

        // Initialize message with stored draft
        // Execute this in a set timeout to run after sendbird initializes the message input
        setTimeout(() => {
            // Ideally we'd sync the innerText to the initialMessage, but doing so causes the cursor to jump to the start of the input. Instead, we only write to the input when its empty (on component initialization) or when initialMessage is empty (which should happen after a message is sent, thus clearing any drafts on other tabs).
            if (messageInputElement.innerText === '' || initialMessage === '') {
                messageInputElement.innerText = initialMessage;
            }
        }, 50);
    }, [channelUrl, initialMessage]);
};
